import { Fragment, useEffect, useState } from "react";

import { SYSTEM_COLORS } from "../../../../components/system.colors";
import { compactCurrencyFormatter, createIntFormatter } from "../../../../utils/formatter/formatter";

import { SectionTitle } from "./SectionTitle";

const intFmt = createIntFormatter();

const pct = (value, digits = 1) => (value === null ? "—" : `${(value * 100).toFixed(digits).replace(/^-(0\.?0*)$/, "$1")}%`);
const dec = (value, digits = 1) => (value === null ? "—" : value.toFixed(digits));
const price = (value) => `R$ ${intFmt.format(Math.round(value))}`;

const COLUMN_GROUPS = [
  { label: "Venda & Resultado", span: 8, background: SYSTEM_COLORS.GREEN.FOREST_GREEN_10, color: SYSTEM_COLORS.GREEN.FOREST_GREEN },
  { label: "Estoque", span: 3, background: "transparent", color: SYSTEM_COLORS.GRAY.CHARCOAL },
  { label: "Preditivo · IA", span: 2, background: SYSTEM_COLORS.ORANGE.PEACH, color: SYSTEM_COLORS.ORANGE.RUST },
];

const COLUMNS = [
  { key: "venda", label: "Venda" },
  { key: "share", label: "Share" },
  { key: "lastYear", label: "Last Year" },
  { key: "vendaPecas", label: "Venda Peças" },
  { key: "precoMedio", label: "Preço Médio" },
  { key: "margem", label: "Margem" },
  { key: "markup", label: "Markup" },
  { key: "giro", label: "Giro" },
  { key: "sellThrough", label: "Sell-through" },
  { key: "capitalEmEstoque", label: "Capital em Estoque" },
  { key: "cobertura", label: "Cobertura" },
  { key: "remarcacao", label: "Remarcação" },
  { key: "metaPercent", label: "% Meta" },
];

const thStyle = {
  padding: "0.6rem 0.8rem",
  fontSize: "0.68rem",
  fontWeight: 700,
  color: SYSTEM_COLORS.GRAY.GREY,
  letterSpacing: "0.3px",
  textAlign: "right",
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
};

const Pill = ({ children, background, color }) => (
  <span style={{
    display: "inline-block",
    padding: "0.2rem 0.55rem",
    borderRadius: "999px",
    background,
    color,
    fontSize: "0.75rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  }}>
    {children}
  </span>
);

const TrendPill = ({ value }) => {
  if (value === null || value === undefined) {
    return <Pill background={SYSTEM_COLORS.WHITE.WHITE_SMOKE} color={SYSTEM_COLORS.GRAY.GREY}>—</Pill>;
  }

  const isUp = value >= 0;
  const background = isUp ? SYSTEM_COLORS.GREEN.FOREST_GREEN_10 : SYSTEM_COLORS.RED.BRICK_RED_10;
  const color = isUp ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED;
  return <Pill background={background} color={color}>{`${isUp ? "+" : ""}${pct(value)}`}</Pill>;
};

const CoberturaPill = ({ dias }) => {
  if (dias === null || dias === undefined) {
    return <Pill background={SYSTEM_COLORS.WHITE.WHITE_SMOKE} color={SYSTEM_COLORS.GRAY.GREY}>—</Pill>;
  }

  const isHealthy = dias <= 110;
  const background = isHealthy ? SYSTEM_COLORS.GREEN.FOREST_GREEN_10 : SYSTEM_COLORS.RED.BRICK_RED_10;
  const color = isHealthy ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED;
  return <Pill background={background} color={color}>{`${Math.round(dias)} dias`}</Pill>;
};

const MetaPill = ({ value }) => {
  if (value === null || value === undefined) {
    return <Pill background={SYSTEM_COLORS.WHITE.WHITE_SMOKE} color={SYSTEM_COLORS.GRAY.GREY}>—</Pill>;
  }

  const isOnTrack = value >= 1;
  const background = isOnTrack ? SYSTEM_COLORS.GREEN.FOREST_GREEN_10 : SYSTEM_COLORS.YELLOW.PALE_YELLOW;
  const color = isOnTrack ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.YELLOW.DARK_OCHRE;
  return <Pill background={background} color={color}>{pct(value)}</Pill>;
};

const SellThroughBar = ({ value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "flex-end" }}>
    <div style={{ inlineSize: "4.5rem", blockSize: "6px", borderRadius: "999px", background: SYSTEM_COLORS.GRAY.CULTURED, overflow: "hidden" }}>
      <div style={{ inlineSize: `${Math.min(value * 100, 100)}%`, blockSize: "100%", background: SYSTEM_COLORS.ORANGE.DEEP_ORANGE, borderRadius: "999px" }} />
    </div>
    <span style={{ fontSize: "0.78rem", color: SYSTEM_COLORS.BLACK.EERIE_BLACK, fontWeight: 500 }}>{pct(value)}</span>
  </div>
);

const Chevron = ({ expanded }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.GREY} strokeWidth="3"
    style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .15s" }}>
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const DataCells = ({ row }) => (
  <>
    <td style={{ ...tdStyle, fontWeight: 700 }}>{compactCurrencyFormatter(row.venda)}</td>
    <td style={tdStyle}><Pill background={SYSTEM_COLORS.ORANGE.BURNT_ORANGE_12} color={SYSTEM_COLORS.ORANGE.BURNT_ORANGE}>{pct(row.share, 2)}</Pill></td>
    <td style={tdStyle}><TrendPill value={row.lastYear} /></td>
    <td style={tdStyle}>{intFmt.format(row.vendaPecas)}</td>
    <td style={tdStyle}>{price(row.precoMedio)}</td>
    <td style={tdStyle}>{pct(row.margem)}</td>
    <td style={tdStyle}>{dec(row.markup, 2)}</td>
    <td style={tdStyle}>{row.giro === null ? "—" : `${dec(row.giro)}x`}</td>
    <td style={tdStyle}><SellThroughBar value={row.sellThrough} /></td>
    <td style={tdStyle}>{compactCurrencyFormatter(row.capitalEmEstoque)}</td>
    <td style={tdStyle}><CoberturaPill dias={row.cobertura} /></td>
    <td style={tdStyle}>{pct(row.remarcacao)}</td>
    <td style={tdStyle}><MetaPill value={row.metaPercent} /></td>
  </>
);

const tdStyle = {
  padding: "0.75rem 0.8rem",
  fontSize: "0.85rem",
  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
  textAlign: "right",
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
};

export function CategoryPerformanceTable({ ctx }) {
  const { categories = [], total, defaultExpandedId = null } = ctx || {};
  const [expanded, setExpanded] = useState(() => new Set(defaultExpandedId ? [defaultExpandedId] : []));

  useEffect(() => {
    setExpanded(new Set(defaultExpandedId ? [defaultExpandedId] : []));
  }, [defaultExpandedId]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <section style={{ marginBlockStart: "2rem" }}>
      <SectionTitle
        title="PERFORMANCE POR CATEGORIA"
        description="Comparativo de venda, share, evolução vs. ano anterior, peças vendidas, preço médio, margem, markup, giro, sell-through, remarcação, capital, cobertura e atingimento de meta por categoria. Expanda cada categoria para ver o detalhe por subgrupo."
      />

      <div style={{
        background: SYSTEM_COLORS.WHITE.WHITE,
        border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
        borderRadius: "14px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        padding: "1.5rem 2rem",
      }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ inlineSize: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...thStyle, textAlign: "left", position: "sticky", left: 0, background: SYSTEM_COLORS.WHITE.WHITE, zIndex: 1 }}>
                Categoria
              </th>
              {COLUMN_GROUPS.map((group) => (
                <th key={group.label} colSpan={group.span} style={{
                  padding: "0.5rem 0.8rem",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  textAlign: "center",
                  color: group.color,
                  background: group.background,
                  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                }}>
                  {group.label.toUpperCase()}
                </th>
              ))}
            </tr>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column.key} style={thStyle}>{column.label.toUpperCase()}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {categories.map((category) => {
              const isExpanded = expanded.has(category.id);

              return (
                <Fragment key={category.id}>
                  <tr>
                    <td style={{
                      ...tdStyle,
                      textAlign: "left",
                      fontWeight: 700,
                      position: "sticky",
                      left: 0,
                      background: SYSTEM_COLORS.WHITE.WHITE,
                    }}>
                      <button
                        onClick={() => toggle(category.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: "0.5rem",
                          background: "none", border: "none", cursor: "pointer",
                          padding: 0, font: "inherit", color: "inherit",
                        }}>
                        <Chevron expanded={isExpanded} />
                        {category.name}
                      </button>
                    </td>
                    <DataCells row={category} />
                  </tr>

                  {isExpanded && category.subgroups.map((subgroup) => (
                    <tr key={subgroup.id} style={{ background: SYSTEM_COLORS.WHITE.WHITE_SMOKE }}>
                      <td style={{
                        ...tdStyle,
                        textAlign: "left",
                        fontWeight: 400,
                        color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY,
                        paddingInlineStart: "2.4rem",
                        position: "sticky",
                        left: 0,
                        background: SYSTEM_COLORS.WHITE.WHITE_SMOKE,
                      }}>
                        {subgroup.name}
                      </td>
                      <DataCells row={subgroup} />
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>

          {total && (
            <tfoot>
              <tr style={{ borderTop: `2px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, background: SYSTEM_COLORS.WHITE.WHITE_SMOKE }}>
                <td style={{ ...tdStyle, textAlign: "left", fontWeight: 700, position: "sticky", left: 0, background: SYSTEM_COLORS.WHITE.WHITE_SMOKE }}>
                  Total
                </td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{compactCurrencyFormatter(total.venda)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{pct(total.share, 0)}</td>
                <td style={{ ...tdStyle, fontWeight: 700, color: total.lastYear === null ? SYSTEM_COLORS.GRAY.GREY : total.lastYear >= 0 ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED }}>
                  {total.lastYear === null ? "—" : `${total.lastYear >= 0 ? "+" : ""}${pct(total.lastYear)}`}
                </td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{intFmt.format(total.vendaPecas)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{price(total.precoMedio)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{pct(total.margem)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{dec(total.markup, 2)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{total.giro === null ? "—" : `${dec(total.giro)}x`}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{pct(total.sellThrough)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{compactCurrencyFormatter(total.capitalEmEstoque)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{total.cobertura === null ? "—" : `${Math.round(total.cobertura)} dias`}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{pct(total.remarcacao)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{total.metaPercent === null ? "—" : pct(total.metaPercent)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p style={{ fontSize: "0.72rem", color: SYSTEM_COLORS.GRAY.GREY, margin: "1rem 0 0" }}>
        Clique em uma categoria para expandir os subgrupos e filtrar a análise.
      </p>
      </div>
    </section>
  );
}
